import { useOutletContext } from '@remix-run/react';
import { useEffect, useMemo, useState } from 'react';

import GatedModal from '~/components/Modal/GatedModal';
import ProgressIndicator from '~/components/ProgressIndicator';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { Post } from '~/interfaces/post';
import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';

import useClipContentHeight from '~/hooks/useClipContentHeight';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useScrollPosition from '~/hooks/useScrollPosition';

import EmailCapturePopup from './EmailCapturePopup';
import FreeGatedContent from './FreeGatedContent';

interface OnScrollActionsProps {
  post: Post;
  publication: Publication;
  isGatedContentDisabled: boolean;
  isPopupDisabled: boolean;
  subscription?: Subscription;
}

const PIXELS_SCROLLED_BEFORE_SHOWING_GATE = 50;
const PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL = 40;

const OnScrollActions = ({ post, publication, isPopupDisabled, isGatedContentDisabled }: OnScrollActionsProps) => {
  const { percentageScrolled, windowScrollY } = useScrollPosition() || {
    percentageScrolled: 0,
    windowScrollY: 0,
  };
  const currentProgress = Number((percentageScrolled * 100).toFixed(1));
  const isLoggedIn = useIsLoggedIn();
  const { setCookie } = useOutletContext<OutletContext>();
  const [isOpen, setIsOpen] = useState(false);
  const [isGateOpen, setIsGateOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const isGated = useMemo(() => {
    return (
      post.enforce_gated_content &&
      !post.contains_subscriber_break &&
      !isGatedContentDisabled &&
      !isPopupDisabled &&
      !isLoggedIn
    );
  }, [post, isGatedContentDisabled, isPopupDisabled, isLoggedIn]);

  useClipContentHeight({
    enabled: mounted && isGated,
  });

  const getElementHeights = () => {
    let contentBounds: any;
    let windowHeight: any;

    if (typeof document !== 'undefined') {
      contentBounds = document.querySelector('.rendered-post')?.getBoundingClientRect();
      windowHeight = window?.innerHeight;
    }

    return { contentBounds, windowHeight };
  };

  const showPopup =
    post.enable_popup_on_scroll &&
    currentProgress >= PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL &&
    !isGatedContentDisabled &&
    !isPopupDisabled &&
    !isLoggedIn;

  const showSubscriberGate = mounted && isGated && windowScrollY >= PIXELS_SCROLLED_BEFORE_SHOWING_GATE;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Ensure popup closes
  useEffect(() => {
    if (isPopupDisabled && isOpen) {
      setIsOpen(false);
    }
  }, [isPopupDisabled, isOpen]);

  // Ensure gate closes
  useEffect(() => {
    if (isGatedContentDisabled && isGateOpen) {
      setIsGateOpen(false);
    }
  }, [isGatedContentDisabled, isGateOpen]);

  // Handle logic around when popup is enabled
  useEffect(() => {
    if (showPopup) {
      setIsOpen(true);
    }
  }, [showPopup]);

  // Handle logic around when gate is enabled
  useEffect(() => {
    setIsGateOpen(showSubscriberGate);
  }, [windowScrollY, showSubscriberGate]);

  useEffect(() => {
    if (!mounted || !isGated) return;

    const { contentBounds, windowHeight } = getElementHeights();

    if (
      (contentBounds &&
        windowHeight &&
        contentBounds.height + windowScrollY < windowHeight - PIXELS_SCROLLED_BEFORE_SHOWING_GATE) ||
      showSubscriberGate
    ) {
      setIsGateOpen(true);
    }
  }, [mounted, isGated, showSubscriberGate, windowScrollY]);

  const handleCloseModal = () => {
    setCookie({ name: 'popup_closed', value: 'true', days: 4 });
    setIsOpen(false);
  };

  return (
    <>
      {!isOpen && !isGateOpen && (
        <div className="sticky top-0 z-50 w-full">
          <ProgressIndicator variant={'post'} currentProgress={currentProgress} />
        </div>
      )}
      {showPopup && (
        <EmailCapturePopup
          isOpen={isOpen}
          onClose={handleCloseModal}
          publication={publication}
          title={post.email_capture_title}
          message={post.email_capture_message}
          cta={post.email_capture_cta}
        />
      )}
      <div className="fixed bottom-0 left-0 z-50 px-4">
        <GatedModal show={isGateOpen}>
          <FreeGatedContent
            title={post.email_capture_title}
            message={post.email_capture_message}
            cta={post.email_capture_cta}
            publication={publication}
            postSlug={post.slug}
            theme="subscribe-gate-bottom"
          />
        </GatedModal>
      </div>
    </>
  );
};

export default OnScrollActions;
